import React, { useMemo } from "react";
import {
  Box,
  Container,
  Grid,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { appTheme } from "shared/theme";

const COLORS = {
  primary: appTheme.palette.green.main,
  secondary: appTheme.palette.gray.main,
  bg: appTheme.palette.white.main,
  softBg: appTheme.palette.gray.light ,
  border: "rgba(0,0,0,0.08)",
};

const textSx = {
  fontFamily: appTheme.typography?.body1?.fontFamily,
  color: COLORS.secondary,
  lineHeight: 1.75,
};

const subheadSx = {
  ...textSx,
  textTransform: "uppercase",
  letterSpacing: "0.12em",
  color: COLORS.primary,
  fontWeight: 800,
  fontSize: { xs: 12, sm: 13, md: 14 },
};

const Title = ({ children }) => (
  <Typography
    variant="h4"
    component="h3"
    sx={{
      fontFamily:
        appTheme.typography?.h1?.fontFamily ||
        appTheme.typography?.body1?.fontFamily,
      textTransform: "uppercase",
      color: COLORS.secondary,
      fontWeight: 900,
      lineHeight: 1.1,
      fontSize: { xs: 22, sm: 26, md: 30 },
      mb: 1,
      letterSpacing: "0.04em",
    }}
  >
    {children}
  </Typography>
);

const ImageFrame = ({ src, alt, compact }) => (
  <Box
    sx={{
      width: "100%",
      borderRadius: { xs: 3, md: 4 },
      overflow: "hidden",
      border: `1px solid ${COLORS.border}`,
      backgroundColor: appTheme.palette.white?.main,
      boxShadow: "0 12px 28px rgba(0,0,0,0.08)",
    }}
  >
    <Box
      component="img"
      src={src}
      alt={alt}
      loading="lazy"
      sx={{
        display: "block",
        width: "100%",
        height: "auto",
        objectFit: "contain",
        maxHeight: compact
          ? { xs: 280, sm: 320, md: 360 }
          : { xs: 360, sm: 440, md: 520 },
      }}
    />
  </Box>
);

const StoreBadges = ({ center }) => (
  <Box
    sx={{
      display: "flex",
      gap: 1.5,
      mt: 3,
      flexWrap: "wrap",
      justifyContent: center ? "center" : "flex-start",
      alignItems: "center",
    }}
  >
    <Box
      component="a"
      href="https://apps.apple.com/au/app/7day-app/id6470282265?itsct=apps_box_link&itscg=30200"
      target="_blank"
      rel="noopener noreferrer"
      sx={{ lineHeight: 0, display: "inline-flex" }}
    >
      <Box
        component="img"
        alt="Download on the App Store"
        src="/appleBadge.jpg"
        sx={{
          height: { xs: 44, sm: 50 },
          width: "auto",
          display: "block",
        }}
      />
    </Box>

    <Box
      component="a"
      href="https://play.google.com/store/apps/details?id=com.sevenday.mobile&pcampaignid=web_share7DAY"
      target="_blank"
      rel="noopener noreferrer"
      sx={{ lineHeight: 0, display: "inline-flex" }}
    >
      <Box
        component="img"
        alt="Get it on Google Play"
        src="/googleBadge.png"
        sx={{
          height: { xs: 44, sm: 50 },
          width: "auto",
          display: "block",
        }}
      />
    </Box>
  </Box>
);

const FeatureSection = ({ section, index }) => {
  const theme = useTheme();
  const mdUp = useMediaQuery(theme.breakpoints.up("md"));
  const reversed = mdUp ? Boolean(section.reverseOnDesktop) : false;

  const isCompact =
    Boolean(section.image?.compact) ||
    section.title.toLowerCase().includes("notifications") ||
    section.title.toLowerCase().includes("pricing") ||
    section.title.toLowerCase().includes("user");

  return (
    <Box
      component="section"
      sx={{
        borderRadius: { xs: 4, md: 6 },
        border: `1px solid ${COLORS.border}`,
        background:
          index % 2 === 0
            ? "linear-gradient(180deg, rgba(92,181,179,0.08) 0%, rgba(255,255,255,1) 55%)"
            : "linear-gradient(180deg, rgba(0,0,0,0.02) 0%, rgba(255,255,255,1) 55%)",
        px: { xs: 2, sm: 3, md: 5 },
        py: { xs: 3, sm: 4, md: 5 },
      }}
    >
      <Grid
        container
        spacing={{ xs: 3, md: 6 }}
        alignItems="center"
        direction={reversed ? "row-reverse" : "row"}
      >
        <Grid item xs={12} md={6}>
          <Box
            sx={{
              display: "flex",
              justifyContent: {
                xs: "center",
                md: reversed ? "flex-start" : "flex-end",
              },
            }}
          >
            <Box
              sx={{
                width: "100%",
                maxWidth: { xs: 520, sm: 640, md: isCompact ? 560 : 680 },
              }}
            >
              <ImageFrame
                src={section.image.src}
                alt={section.image.alt}
                compact={isCompact}
              />
            </Box>
          </Box>
        </Grid>

        <Grid item xs={12} md={6}>
          <Box
            sx={{
              maxWidth: 680,
              mx: { xs: "auto", md: 0 },
              textAlign: { xs: "center", md: "left" },
            }}
          >
            {section.kicker && <Typography sx={subheadSx}>{section.kicker}</Typography>}

            <Title>{section.title}</Title>

            <Box sx={{ mt: 2 }}>
              {section.paragraphs.map((p, i) => (
                <Typography
                  key={i}
                  sx={{
                    ...textSx,
                    mb: i === section.paragraphs.length - 1 ? 0 : 1.75,
                    fontSize: { xs: 14.5, sm: 15.5, md: 16 },
                  }}
                >
                  {p}
                </Typography>
              ))}
            </Box>

            {section.hasStoreBadges && <StoreBadges center={!mdUp} />}
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export const ProductContentContractors = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "lg"));

  const headerVariant = useMemo(
    () => (isMobile ? "h3" : isTablet ? "h2" : "h1"),
    [isMobile, isTablet],
  );

  const sections = [
    {
      title: "Receive Online Bookings",
      kicker: "New jobs drop in — tap accept and you’re booked.",
      paragraphs: [
        "Get notified the moment a new job request comes through, review the details, and accept in seconds. Once confirmed, the job is added to your schedule with all the info you need—time, location, notes, and what’s included—so you can show up prepared and deliver smoothly.",
      ],
      image: {
        src: "/imgs/classic/contractors/jobrequest.png",
        alt: "job request",
      },
      hasStoreBadges: true,
    },
    {
      title: "Manage your bookings",
      kicker: "Stay in control of your team’s bookings—past, present, and future.",
      paragraphs: [
        "See all your upcoming jobs in one place with the 7DAY built-in calendar. View booking details at a glance, stay on top of what’s next, and avoid double-bookings with a schedule that updates automatically as new jobs come in.",
        "Perfect for contractors juggling multiple clients, locations, and time slots.",
      ],
      image: {
        src: "/imgs/classic/contractors/calendar.png",
        alt: "Manage bookings calendar",
      },
      reverseOnDesktop: true,
    },
    {
      title: "Add your services",
      kicker: "Showcase what you offer and where you work.",
      paragraphs: [
        "Create a professional service profile by adding the services you provide, from catering and wellness services to team building activities, transport, and more. Clearly outline your offerings so Hosts can quickly understand how you can support their experience.",
        "Set the areas you service to help Hosts & Guests find the right contractor in the right location. Whether you operate locally, regionally, or nationwide, your services will be matched with relevant opportunities in your preferred service areas.",
      ],
      image: {
        src: "/imgs/classic/contractors/services.png",
        alt: "Add contractor services and service areas",
      },
      reverseOnDesktop: false,
    },
    {
      title: "Manage your availability",
      kicker: "Let Hosts & Guests know when you're available to work.",
      paragraphs: [
        "Set your regular availability by choosing the days and times you’re available to accept bookings. Keep your schedule up to date so Hosts & Guests can confidently engage your services for upcoming experiences.",
        "You can also block out specific dates when you're unavailable, such as holidays, personal commitments, or existing bookings. Your availability is automatically reflected across the platform, helping reduce scheduling conflicts and saving time.",
      ],
      image: {
        src: "/imgs/classic/contractors/availability.png",
        alt: "Contractor availability management",
      },
      reverseOnDesktop: true,
    },
    {
      title: "Real-time notifications",
      kicker: "Stay informed without lifting a finger.",
      paragraphs: [
        "Our in-app notification center keeps you updated on all booking activity as it happens—from approvals and declines to cancellations.",
        "With real-time alerts, you’ll spend less time chasing updates and more time focusing on what matters.",
      ],
      image: {
        src: "/imgs/classic/contractors/notifications.jpg",
        alt: "Notifications",
        compact: true,
      },
    },
    {
      title: "User Management",
      kicker: "Easily manage who can access and use 7DAY across your team.",
      paragraphs: [
        "Invite staff securely, assign roles, and control access levels with ease. Whether you’re adding new team members or adjusting permissions, our user management tools put you in control.",
      ],
      image: {
        src: "/imgs/classic/contractors/users.png",
        alt: "User management",
        compact: true,
      },
      reverseOnDesktop: true,
    },
    {
      title: "Pricing Templates",
      kicker: "Set your prices once — your services sell themselves.",
      paragraphs: [
        "Set up pricing templates that showcase exactly what you offer—your services, inclusions, and rates—so hosts and guests can view options upfront and book with confidence. Keep your pricing consistent, transparent, and ready to go for every job request.",
      ],
      image: {
        src: "/imgs/classic/contractors/pricing.png",
        alt: "Pricing templates",
        compact: true,
      },
    },
    {
      title: "In-App Messaging",
      kicker: "Connect directly with hosts",
      paragraphs: [
        "Our built-in messenger makes it easy to ask questions, confirm details, or coordinate plans with the host. Whether you're enquiring about a venue or discussing an upcoming job, everything stays in one place—secure, simple, and streamlined.",
        "No more chasing emails or phone calls—just clear, real-time communication right inside the 7DAY app.",
      ],
      image: {
        src: "/imgs/classic/contractors/chat.jpg",
        alt: "In-app messaging",
      },
      reverseOnDesktop: true,
    },
  ];

  return (
    <Box sx={{ backgroundColor: COLORS.bg }}>
      <Box
        sx={{
          background:
            "radial-gradient(900px 500px at 15% 0%, rgba(92,181,179,0.16) 0%, rgba(255,255,255,0) 60%), radial-gradient(800px 420px at 85% 0%, rgba(0,0,0,0.05) 0%, rgba(255,255,255,0) 55%)",
        }}
      >
        <Container
          id="product"
          maxWidth="lg"
          sx={{
            pt: { xs: 14, md: 20 },
            pb: { xs: 6, md: 10 },
          }}
        >
          <Box sx={{ textAlign: "center", maxWidth: 980, mx: "auto" }}>
            <Typography
              component={headerVariant}
              variant={headerVariant}
              sx={{
                color: COLORS.secondary,
                fontFamily:
                  appTheme.typography?.h1?.fontFamily ||
                  appTheme.typography?.body1?.fontFamily,
                textTransform: "uppercase",
                fontWeight: 900,
                letterSpacing: "0.06em",
                lineHeight: 1.05,
                fontSize: { xs: 34, sm: 44, md: 56 },
              }}
            >
              Contractor Features
            </Typography>

            <Typography
              variant="h6"
              component="h2"
              sx={{
                ...textSx,
                mt: 1.25,
                textTransform: "uppercase",
                letterSpacing: "0.12em",
                color: COLORS.primary,
                fontWeight: 800,
                fontSize: { xs: 12, sm: 13, md: 14 },
              }}
            >
              The only app you need to accept jobs, track bookings, and stay paid
            </Typography>

            <Box
              sx={{
                mt: 3,
                height: 1,
                width: { xs: "100%", sm: 640 },
                mx: "auto",
                backgroundColor: COLORS.border,
              }}
            />
          </Box>

          <Box
            sx={{
              mt: { xs: 5, md: 7 },
              display: "flex",
              flexDirection: "column",
              gap: { xs: 3, md: 4 },
            }}
          >
            {sections.map((section, index) => (
              <FeatureSection key={section.title} section={section} index={index} />
            ))}
          </Box>
        </Container>
      </Box>

      <Box
        sx={{
          backgroundColor: COLORS.softBg,
          borderTop: `1px solid ${COLORS.border}`,
          py: { xs: 6, md: 8 },
        }}
      >
        <Container maxWidth="lg">
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              gap: { xs: 2, md: 3 },
              alignItems: { xs: "center", md: "center" },
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ textAlign: { xs: "center", md: "left" } }}>
              <Typography
                sx={{
                  fontFamily:
                    appTheme.typography?.h1?.fontFamily ||
                    appTheme.typography?.body1?.fontFamily,
                  textTransform: "uppercase",
                  fontWeight: 900,
                  color: COLORS.secondary,
                  fontSize: { xs: 18, sm: 20, md: 22 },
                  lineHeight: 1.2,
                }}
              >
              Ready to accept more jobs—without the admin?
              </Typography>
              <Typography sx={{ ...textSx, mt: 1, maxWidth: 680 }}>
              Get requests, confirm bookings, and manage your schedule in one place.
              </Typography>
            </Box>

            <StoreBadges center />
          </Box>
        </Container>
      </Box>
    </Box>
  );
};
